import { identify } from './th'

// Single client-side identity surface: session user (/api/me) plus the stored
// gateway API key ('appnz_gateway_key', same slot GatewayPlayground persists).
// authedFetch attaches both — cookie session always, bearer key when present —
// so every caller (playgrounds, spaces, canvas, e2e) authenticates the same way.
// Also mirrored to window.userData for e2e and console use.

export interface UserData {
  authenticated: boolean
  user: { id: string; email?: string; name?: string; is_admin?: boolean } | null
  handle: string
  apiKey: string
}

const GATEWAY_KEY_SLOT = 'appnz_gateway_key'

let cache: UserData | null = null
let inflight: Promise<UserData> | null = null

export function storedApiKey(): string {
  try {
    return localStorage.getItem(GATEWAY_KEY_SLOT) || ''
  } catch {
    return ''
  }
}

export function setStoredApiKey(key: string): void {
  try {
    if (key) localStorage.setItem(GATEWAY_KEY_SLOT, key)
    else localStorage.removeItem(GATEWAY_KEY_SLOT)
  } catch { /* storage unavailable */ }
  if (cache) {
    cache = { ...cache, apiKey: key }
    mirror(cache)
  }
}

function mirror(data: UserData) {
  try {
    ;(window as any).userData = data
  } catch { /* SSR */ }
}

export async function getUserData(force = false): Promise<UserData> {
  if (cache && !force) return cache
  if (inflight && !force) return inflight
  inflight = (async () => {
    let authenticated = false
    let user: UserData['user'] = null
    let handle = ''
    try {
      const res = await fetch('/api/me', { credentials: 'include' })
      const body = await res.json().catch(() => ({}))
      authenticated = !!body?.authenticated
      user = body?.user || null
      handle = body?.handle || ''
    } catch { /* offline — report signed out */ }
    cache = { authenticated, user, handle, apiKey: storedApiKey() }
    mirror(cache)
    if (authenticated && user?.id) identify(user.id)
    inflight = null
    return cache
  })()
  return inflight
}

export function invalidateUserData(): void {
  cache = null
  inflight = null
}

// fetch with the full identity attached: session cookie always; stored gateway
// API key as a bearer when the caller hasn't set its own Authorization.
export function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {})
  const key = storedApiKey()
  if (key && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${key}`)
  return fetch(input, { ...init, headers, credentials: init.credentials || 'include' })
}
